import type { TechnicianSkillMapping } from '../user-management/types';

export type OptionDefaultType = Array<{
  label: string;
  value: string;
}>;

export type InitialLookUpStateType = {
  isFetchingLookUpOptions: boolean;
  /** `true` when the most recent look-up fetch rejected; cleared when the next fetch starts.
   *  Optional so existing `useLookUpApi` mocks/consumers don't need updating — absent ⇒ no error. */
  hasLookUpError?: boolean;
  /** Markets-scoped counterpart of `hasLookUpError`; non-markets look-ups do not affect it. */
  hasMarketsLookUpError?: boolean;
  lookupData: {
    /** `state_code` is the two-letter code (`KS`) the address State select shows; `label` is the full name. */
    states?: Array<{ label: string; value: string; state_code?: string | null }>;
    countries?: OptionDefaultType;
    priorities?: OptionDefaultType;
    access_methods?: OptionDefaultType;
    pet_types?: OptionDefaultType;
    materialQuantityType?: OptionDefaultType;
    problem_categories?: TechnicianSkillMapping;
    technicians?: {
      label: string;
      /** The TECHNICIAN uuid, not the user's — what `api/technicians/{id}/...` takes. */
      value: string;
      user_id: string;
      timezone: string;
    }[];
    /**
     * Assumed contract — `LookupController` has no `vendors` arm yet, so this stays
     * empty and only `SkillSetCopyControl` reads it. `value` is the id
     * `api/vendors/{id}/skills` binds; `user_id` is the account's uuid.
     */
    vendors?: {
      label: string;
      value: string;
      user_id: string;
    }[];
    assignees?: Array<{ name: string; profile_pic: string; user_id: string }>;
    coordinators?: Array<{
      user_id: string;
      name: string;
      email?: string;
      assigned_market_count: number;
      profile_pic: string | null;
    }>;
    properties?: Array<{
      property_id: string;
      address: string;
      zip_code: string;
      latitude: string;
      longitude: string;
    }>;
    end_trip_options?: {
      no_work: OptionDefaultType;
      partial_complete: OptionDefaultType;
      work_type: OptionDefaultType;
    };
    markets?: OptionDefaultType;
    reschedule_reasons?: {
      reschedule_reason_id: number;
      label: string;
      slug: string;
      reschedule_reason_options: {
        reschedule_reason_id: number;
        label: string;
        slug: string;
      }[];
    }[];
  };
};

export const lookUpInitialState: InitialLookUpStateType = {
  isFetchingLookUpOptions: false,
  hasLookUpError: false,
  hasMarketsLookUpError: false,
  lookupData: {
    states: [],
    countries: [],
    problem_categories: [],
    priorities: [],
    access_methods: [],
    pet_types: [],
    technicians: [],
    vendors: [],
    assignees: [],
    coordinators: [],
    properties: [],
    materialQuantityType: [
      { label: 'Linear ft', value: 'linear-ft' },
      { label: 'Pounds', value: 'pounds' },
      { label: 'Square ft', value: 'square-ft' },
      { label: 'Units', value: 'units' },
    ],
    end_trip_options: {
      no_work: [],
      partial_complete: [],
      work_type: [],
    },
    reschedule_reasons: [],
    markets: [],
  },
};
